/* eslint-disable no-unused-vars */
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";

//import { projectVerticesToPlane } from "@/class/projectVerticesToPlane";
import pako from "pako";

import { applyColorsToMeshUsingTargets } from "@/class/agMapBuilder";
//import { createMedialDistalCylinders } from "@/components/methods/cylinderMethods";

export default {
  methods: {
    moveDentalToIdealPosition() {
      // Sorteia um dos dentes
      const dentalKeys = Object.keys(this.dentalMap);
      // Usa o módulo para lidar com o contador ultrapassando o número de chaves
      const toothKeyIndex = this.toothIndexCounter % dentalKeys.length;
      const selectedToothKey = dentalKeys[toothKeyIndex];

      // Incrementa o contador para o próximo dente na sequência
      this.toothIndexCounter += 1;

      const selectedTooth = this.dentalMap[selectedToothKey];

      if (selectedTooth.borderPoints.length === 0) return;

      let selectedPointIndex;
      let closestDistance = Infinity;
      let closestPoint = null;

      // Encontra o ponto mais próximo e seu índice
      this.idealArchPoints.forEach((point, index) => {
        const dist = this.distanceBetweenPoints(
          point,
          selectedTooth.centerOfMass
        );
        if (dist < closestDistance) {
          closestDistance = dist;
          closestPoint = point;
          selectedPointIndex = index;
        }
      });

      // Verifica se precisamos nos mover mais próximo ou mais distante do vizinho
      const needsToBeCloser = closestDistance > 0.5;
      const needsToBeFarther = closestDistance < 0.2;

      let newIdealPoint = closestPoint;
      let prevPointDistance = Infinity;
      let nextPointDistance = Infinity;

      // Calcula a distância do ponto anterior e posterior
      if (selectedPointIndex > 0) {
        prevPointDistance = this.distanceBetweenPoints(
          this.idealArchPoints[selectedPointIndex - 1],
          selectedTooth.centerOfMass
        );
      }
      if (selectedPointIndex < this.idealArchPoints.length - 1) {
        nextPointDistance = this.distanceBetweenPoints(
          this.idealArchPoints[selectedPointIndex + 1],
          selectedTooth.centerOfMass
        );
      }

      // Define o novo ponto ideal com base na necessidade de se aproximar ou se afastar
      if (needsToBeCloser) {
        // Escolhe o ponto que está mais próximo do centro de massa
        newIdealPoint =
          prevPointDistance < nextPointDistance
            ? this.idealArchPoints[selectedPointIndex - 1]
            : this.idealArchPoints[selectedPointIndex + 1];
      } else if (needsToBeFarther) {
        // Escolhe o ponto que está mais longe do centro de massa
        newIdealPoint =
          prevPointDistance > nextPointDistance
            ? this.idealArchPoints[selectedPointIndex - 1]
            : this.idealArchPoints[selectedPointIndex + 1];
      }

      // Calcula o movimento necessário em x e z, mas limita a 0.1
      let moveX = Math.min(
        Math.max(newIdealPoint.x - selectedTooth.centerOfMass.x, -0.1),
        0.1
      );
      let moveZ = Math.min(
        Math.max(newIdealPoint.z - selectedTooth.centerOfMass.z, -0.1),
        0.1
      );

      // Atualiza o centro de massa
      selectedTooth.centerOfMass.x += moveX;
      selectedTooth.centerOfMass.z += moveZ;

      // Encontra e atualiza o dente na cena
      const teethInScene = this.findTeethInScene();
      const toothInScene = teethInScene.find(
        (t) => t.userData.toothNumber == selectedToothKey
      );

      if (toothInScene) {
        const originalPosition = toothInScene.position.clone();
        const originalRotation = toothInScene.rotation.clone();

        toothInScene.position.x = selectedTooth.centerOfMass.x;
        toothInScene.position.z = selectedTooth.centerOfMass.z;

        const lastDigit = parseInt(dentalKeys[toothKeyIndex]) % 10;

        if (lastDigit >= 5) {
          // Aplica a rotação em X e Z em direção a 90 graus
          selectedTooth.actualAngles.x = this.adjustAngleTowards90(
            selectedTooth.actualAngles.x
          );
          selectedTooth.actualAngles.z = this.adjustAngleTowards90(
            selectedTooth.actualAngles.z
          );

          // Aplica as rotações no objeto da cena
          toothInScene.rotation.x = this.degreesToRadians(
            selectedTooth.angles.x - selectedTooth.actualAngles.x
          );
          toothInScene.rotation.z = this.degreesToRadians(
            selectedTooth.angles.z - selectedTooth.actualAngles.z
          );
        }
        // Verifica colisões para cada vizinho
        const adjacentTeeth = this.getAdjacentTeeth(parseInt(selectedToothKey));
        let collision = false;

        for (let i = 0; i < adjacentTeeth.length; i++) {
          const result = this.calculateClosestDistanceBetweenTeeth(
            selectedToothKey,
            adjacentTeeth[i]
          );

          // Se houver colisão ou a distância mínima não for atendida, reverte as transformações
          if (result && result.collisionDetected) {
            collision = true;
            break; // Sai do loop se uma colisão for detectada
          }
        }

        // Se uma colisão foi detectada, reverte as mudanças
        if (collision) {
          toothInScene.position.copy(originalPosition);
          toothInScene.rotation.copy(originalRotation);
        }
      }
    },
    newCreatingDentalArch(params) {
      this.activeDentalArch = params.arch;
      this.idealArchStartPoints[this.activeDentalArch] = params.points;

      const curve = new THREE.CatmullRomCurve3(
        params.points,
        false,
        "catmullrom",
        0.5
      );
      const curvePoints = curve.getPoints(256);
      this.idealArchPoints[this.activeDentalArch] = curvePoints;

      // Atualiza a cena e a data no Vuex
      this.$store.dispatch("editor3D/updateScene", this.scene);
      this.sceneUpdate = new Date();
      this.$store.dispatch("editor3D/updateDate", this.sceneUpdate);
      this.clearContourPoints();
    },
    creatingDentalArch(params) {
      // Extrai os pontos dos parâmetros
      const { points } = params;

      // Extrai os pontos diretamente usando map para melhorar a legibilidade
      const pointsCoordinates = points.map((p) => ({
        x: p.point.x,
        z: p.point.z,
      }));

      // Calcula os coeficientes da parábola no plano xz
      const coefficients = this.calculateParabolaCoefficients(
        pointsCoordinates[0].x,
        pointsCoordinates[0].z, // Coordenadas do leftMolar
        pointsCoordinates[1].x,
        pointsCoordinates[1].z, // Coordenadas do incisor
        pointsCoordinates[2].x,
        pointsCoordinates[2].z // Coordenadas do rightMolar
      );

      // Gera um conjunto de pontos ao longo da parábola usando os coeficientes calculados
      // Você pode decidir sobre o número de pontos que deseja gerar na parábola
      const archPoints = this.generateArchPoints(
        pointsCoordinates[0].x,
        pointsCoordinates[2].x,
        coefficients,
        160
      );

      // Converte os pontos de Vector2 para Vector3, assumindo y=0 pois a parábola está no plano xz
      const curvePoints = archPoints.map((p) => new THREE.Vector3(p.x, 0, p.z));

      this.idealArchPoints = curvePoints;

      // Encontra ou cria um grupo para a parábola
      let parent = this.scene.getObjectByName("Dental Arch");
      if (!parent) {
        parent = new THREE.Group();
        parent.name = "Dental Arch";
        parent.userData.name = this.$t("idealDentalArch");
        this.scene.add(parent);
      } else {
        // Se o grupo já existir, remove os filhos antigos
        while (parent.children.length > 0) {
          const child = parent.children[0];
          parent.remove(child);
          // Limpando geometria e material do child, se necessário
          if (child.geometry) child.geometry.dispose();
          if (child.material) child.material.dispose();
        }
      }

      // Usa a função addLinesBetweenPoints para adicionar o tubo ao grupo 'Dental Arch'
      //this.addLinesBetweenPoints(curvePoints, parent, false, 0.3, 0x00ff00); // false indica que a curva não é fechada

      // Adiciona esferas ao grupo 'Dental Arch'
      const sphereGeometry = new THREE.SphereGeometry(0.5, 4, 4);
      const sphereMaterial = new THREE.MeshPhongMaterial({
        color: 0x2652ff,
        specular: 0x222222,
        shininess: 400,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.5,
      });
      archPoints.forEach((point) => {
        const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
        sphere.position.copy(point);
        parent.add(sphere);
      });

      // Atualiza a cena e a data no Vuex
      this.$store.dispatch("editor3D/updateScene", this.scene);
      this.sceneUpdate = new Date();
      this.$store.dispatch("editor3D/updateDate", this.sceneUpdate);
    },
    generateArchPoints(startX, endX, coeficients, numPoints) {
      let points = [];
      let step = (endX - startX) / (numPoints - 2);

      for (let i = 0; i < numPoints + 3; i++) {
        let x = startX + step * i - step * 2;
        let z = coeficients.a * x * x + coeficients.b * x + coeficients.c;
        points.push(new THREE.Vector3(x, 0, z)); // Assumimos que o arco está alinhado com o plano y = 0
      }

      return points;
    },
    calculateParabolaCoefficients(x1, y1, x2, y2, x3, y3) {
      // Primeiro, montamos a matriz A usando os valores de x dos pontos
      let A = [
        [x1 * x1, x1, 1],
        [x2 * x2, x2, 1],
        [x3 * x3, x3, 1],
      ];

      // Função para calcular o determinante de uma matriz 3x3
      function determinant(M) {
        return (
          M[0][0] * (M[1][1] * M[2][2] - M[1][2] * M[2][1]) -
          M[0][1] * (M[1][0] * M[2][2] - M[1][2] * M[2][0]) +
          M[0][2] * (M[1][0] * M[2][1] - M[1][1] * M[2][0])
        );
      }

      // Função para substituir uma coluna de uma matriz por um vetor
      function substituteColumn(M, col, B) {
        return M.map((row, i) => {
          let newRow = row.slice();
          newRow[col] = B[i];
          return newRow;
        });
      }

      // Depois, montamos a matriz B usando os valores de y dos pontos
      let B = [y1, y2, y3];

      // Usamos a regra de Cramer para resolver para a, b e c
      let detA = determinant(A);
      let detAa = determinant(substituteColumn(A, 0, B));
      let detAb = determinant(substituteColumn(A, 1, B));
      let detAc = determinant(substituteColumn(A, 2, B));

      // Se o determinante de A for zero, a matriz é singular e o sistema não tem uma solução única
      if (detA === 0) {
        throw new Error(
          "Singular matrix, cannot solve for parabola coefficients."
        );
      }

      let a = detAa / detA;
      let b = detAb / detA;
      let c = detAc / detA;

      return { a, b, c };
    },
    processPendingTasks() {
      this.tasks.forEach((task) => {
        if (!task.executed) {
          this.processTask(task);
        }
      });
    },
    processExecutedTasks() {
      this.tasks.forEach((task) => {
        if (task.executed) {
          this.processTask(task);
        }
      });
    },
    calculateNormalFromVertices(vertices, index) {
      // Obter os três vértices do triângulo
      let p1 = new THREE.Vector3(
        vertices[index],
        vertices[index + 1],
        vertices[index + 2]
      );
      let p2 = new THREE.Vector3(
        vertices[index + 3],
        vertices[index + 4],
        vertices[index + 5]
      );
      let p3 = new THREE.Vector3(
        vertices[index + 6],
        vertices[index + 7],
        vertices[index + 8]
      );

      // Calcular as arestas do triângulo
      let edge1 = new THREE.Vector3().subVectors(p2, p1);
      let edge2 = new THREE.Vector3().subVectors(p3, p1);

      // Usar o produto vetorial das arestas para calcular a normal
      let normal = new THREE.Vector3().crossVectors(edge1, edge2).normalize();
      return normal;
    },
    async dbSaveDentalMap(modelId, dentalMap) {
      try {
        let response = await fetch(`${process.env.VUE_APP_API_URL}dentalmaps`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ modelId, dentalMap }),
        });

        let result = await response.json();
        if (response.ok) {
          console.log("DentalMap salvo com sucesso:", result);
        } else {
          console.error("Erro ao salvar o dentalMap:", result);
        }
      } catch (error) {
        console.error("Erro ao salvar o dentalMap:", error);
      }
    },
    async saveAnalyzedSphereData() {
      const modelId = this.activeObject.userData.modelId;
      try {
        // Executa a análise das esferas para obter os dados
        const analyzedData = this.analyzeSpheres(
          this.activeObject.geometry,
          0.25
        );

        // Faz o salvamento dos dados gerados usando dbSaveDentalMap
        await this.dbSaveBorderInfo(modelId, analyzedData);
      } catch (error) {
        console.error("Erro ao salvar os dados das esferas:", error);
      }
    },
    async dbSaveBorderInfo(modelId, borderInfo) {
      try {
        let response = await fetch(`${process.env.VUE_APP_API_URL}borderinfo`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ modelId, borderInfo }),
        });

        let result = await response.json();
        if (response.ok) {
          console.log("BorderInfo salvo com sucesso:", result);
          alert("Dados Salvos com Sucesso - ID " + result.borderInfoId);
        } else {
          console.error("Erro ao salvar o BorderInfo:", result);
        }
      } catch (error) {
        console.error("Erro ao salvar o BorderInfo:", error);
      }
    },
    async dbSaveJsonData(structureType, jsonData, Id = null) {
      const modelId = Id === null ? this.activeObject.userData.modelId : Id;

      // Compactar JSON antes de enviar ao backend como Blob
      async function compressJsonToBlob(jsonData) {
        const jsonString = JSON.stringify(jsonData);
        const compressed = pako.gzip(jsonString);
        return new Blob([compressed], { type: "application/gzip" });
      }

      try {
        const dataToSave = await compressJsonToBlob(jsonData);

        // Criar FormData para envio como arquivo Blob
        const formData = new FormData();
        formData.append("modelId", modelId);
        formData.append("structureType", structureType);
        formData.append("jsonData", dataToSave, "data.gz");

        let response = await fetch(`${process.env.VUE_APP_API_URL}jsondata`, {
          method: "POST",
          body: formData,
        });

        let result = await response.json();
        if (response.ok) {
          console.log("JSON salvo com sucesso:", result);
          return true;
        } else {
          console.error("Erro ao salvar JSON:", result);
          return false;
        }
      } catch (error) {
        console.error("Erro ao salvar JSON:", error);
        return false;
      }
    },

    async dbLoadJsonData(structureType, Id = null) {
      const modelId = Id === null ? this.activeObject.userData.modelId : Id;

      // Descompactar JSON após o download
      async function decompressJsonFromBlob(blob) {
        const arrayBuffer = await blob.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        const decompressed = pako.ungzip(uint8Array, { to: "string" });
        return JSON.parse(decompressed);
      }

      try {
        let response = await fetch(
          `${process.env.VUE_APP_API_URL}jsondata/${modelId}/${structureType}`
        );
        let data = await decompressJsonFromBlob(await response.blob());

        if (response.ok) {
          console.log("JSON carregado com sucesso");
          return data;
        } else {
          console.error("Erro ao carregar JSON", data);
          return false;
        }
      } catch (error) {
        console.error("Erro ao carregar JSON:", error);
        return false;
      }
    },
    async loadAndCreateBorderInfoMesh() {
      const modelId = this.activeObject.userData.modelId;
      try {
        // Carregar os dados do borderInfo a partir do banco
        const borderInfoData = await this.dbLoadBorderInfo(modelId);

        if (!borderInfoData) {
          console.error("Nenhum dado encontrado para o BorderInfo.");
          return;
        }

        // Verificar se o mesh 'borderInfo' já existe
        let borderInfo = this.scene.getObjectByName("borderInfo");

        // Se existir, limpar todas as esferas internas (filhos)
        if (borderInfo) {
          while (borderInfo.children.length > 0) {
            const child = borderInfo.children.pop();
            child.geometry.dispose();
            child.material.dispose();
          }
        } else {
          // Se não existir, criar um novo grupo 'borderInfo'
          borderInfo = new THREE.Group();
          borderInfo.name = "borderInfo";
          this.scene.add(borderInfo);
        }

        // Recriar as esferas baseadas nos dados recebidos do banco
        borderInfoData.forEach((sphereData) => {
          const position = new THREE.Vector3(
            sphereData.position.x,
            sphereData.position.y,
            sphereData.position.z
          );
          const radius = 0.25; // Pode ajustar de acordo com seus requisitos
          const color = 0x00ff00; // Usando a mesma cor da rotina createTranslucentSphere
          const opacity = 0.5; // Usando a mesma opacidade

          // Criar a esfera e adicioná-la ao grupo 'borderInfo'
          this.createTranslucentSphere(position, radius, color, opacity);
        });

        console.log("BorderInfo mesh recriado com sucesso.");
      } catch (error) {
        console.error("Erro ao recriar o BorderInfo mesh:", error);
      }
    },
    async loadAndCreateBorderInfoMeshSimple() {
      const modelId = this.activeObject.userData.modelId;
      try {
        // Carregar os dados do borderInfo a partir do banco
        const borderInfoData = await this.dbLoadBorderInfo(modelId);

        if (!borderInfoData) {
          this.gumSearch();
          return;
        }
        const sphereData = this.analyzeArrayBVH(
          this.activeObject.geometry,
          borderInfoData,
          0.45
        );

        const perfectMesh = applyColorsToMeshUsingTargets(
          this.activeObject,
          sphereData
        );
        this.newToothSegmenter(perfectMesh);
      } catch (error) {
        console.error("Erro ao recriar o mesh:", error);
      }
    },
    async dbLoadBorderInfo(modelId) {
      try {
        let response = await fetch(
          `${process.env.VUE_APP_API_URL}borderinfo/${modelId}`
        );
        let result = await response.json();

        if (response.ok) {
          return result.borderInfo;
        } else {
          console.error("Erro :", result);
          return false;
        }
      } catch (error) {
        console.error("Erro :", error);
        return false;
      }
    },
    async dbLoadTrainingCounter() {
      try {
        // 1. Carregar availableSets do backend
        let setsResponse = await fetch(
          `${process.env.VUE_APP_API_URL}available-sets`
        );
        let setsResult = await setsResponse.json();

        if (!setsResponse.ok) {
          console.error("Erro ao carregar availableSets:", setsResult);
          return false;
        }

        // Adiciona a opção fixa "Inserir Novo Conjunto"
        this.availableSets = [
          ...setsResult,
          { value: "NEW", title: "Inserir Novo Conjunto" },
        ];

        // 2. Carregar structure-type-count do backend
        let counterResponse = await fetch(
          `${process.env.VUE_APP_API_URL}structure-type-count`
        );
        let counterResult = await counterResponse.json();

        if (!counterResponse.ok) {
          console.error(
            "Erro ao carregar structure-type-count:",
            counterResult
          );
          return false;
        }

        this.backendData = counterResult;

        // 3. Combinar os dados
        this.updatedSets = this.availableSets.map((set) => {
          if (set.value === "NEW") {
            return { ...set }; // Mantém "Inserir Novo Conjunto" sem contador
          }

          const matchingBackendData = this.backendData.find(
            (data) => data.structureType === `TENSOR_${set.value}`
          );
          return {
            ...set,
            title: matchingBackendData
              ? `Conjunto ${set.value} - ${set.title} (${matchingBackendData.count})`
              : `Conjunto ${set.value} - ${set.title} (0)`, // Caso não haja dado correspondente
          };
        });

        console.log(
          "availableSets e structure-type-count carregados com sucesso"
        );
        return true;
      } catch (error) {
        console.error("Erro ao carregar dados:", error);
        return false;
      }
    },
    handleSetSelection(value) {
      if (value === "NEW") {
        this.addNewSet();
      } else {
        console.log("Selecionado:", value);
      }
    },
    async addNewSet() {
      try {
        let response = await fetch(
          `${process.env.VUE_APP_API_URL}available-sets`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              title: ``,
            }),
          }
        );

        let result = await response.json();

        if (response.ok) {
          console.log("Novo conjunto adicionado com sucesso:", result);
          await this.dbLoadTrainingCounter(); // Recarrega a lista após adicionar
          // Definir o conjunto recém-adicionado como o ativo
          this.selectedSet = result.set.value; // Supondo que `result.value` seja o identificador do conjunto
        } else {
          console.error("Erro ao adicionar novo conjunto:", result);
        }
      } catch (error) {
        console.error("Erro ao adicionar novo conjunto:", error);
      }
    },
    async dbLoadDentalMap(modelId) {
      try {
        let response = await fetch(
          `${process.env.VUE_APP_API_URL}dentalmaps/${modelId}`
        );
        let result = await response.json();

        if (response.ok) {
          console.log("DentalMap carregado com sucesso:", result.dentalMap);
          return result.dentalMap;
        } else {
          console.error("Erro ao carregar o dentalMap:", result);
          return false;
        }
      } catch (error) {
        console.error("Erro ao carregar o dentalMap:", error);
        return false;
      }
    },
    async dbSearchModel(modelName, fileName, fileSize, geometry) {
      try {
        const url = `${
          process.env.VUE_APP_API_URL
        }models/search?name=${encodeURIComponent(
          modelName
        )}&fileName=${encodeURIComponent(
          fileName
        )}&fileSize=${encodeURIComponent(fileSize)}`;

        let response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        let result = await response.json();
        console.table(result);

        if (response.ok) {
          const modelId = result.modelId;

          // Procurar o mesh na scene
          this.scene.traverse((object) => {
            if (
              object.isMesh &&
              object.userData.fromFile &&
              object.userData.modelType === modelName
            ) {
              object.userData.modelId = modelId;
              console.log(
                `ModelId ${modelId} added to mesh with modelType ${modelName}`
              );
            }
          });

          if (result.dentalMap) {
            this.$refs.progressDialog.showProgressDialog(
              "Carregando Segmentação. Aguarde...",
              10
            );
            setTimeout(() => {
              this.updateDentalMapAfterLoading(result.dentalMap, modelId);
            }, 1000);
          }
        } else {
          console.error("Erro ao procurar modelo:", result);
          return await this.dbSaveModel(
            modelName,
            fileName,
            fileSize,
            geometry
          ); // Chama o método para salvar o modelo;
        }
      } catch (error) {
        console.error("Erro ao procurar modelo:", error);
        return false;
      }
    },
    async dbSaveModel(modelName, fileName, fileSize, threeJsGeometry) {
      const vertices = threeJsGeometry.attributes.position.array;
      const geometry = { attributes: { position: { array: vertices } } };

      try {
        const response = await fetch(`${process.env.VUE_APP_API_URL}models`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: modelName,
            fileName,
            fileSize,
            geometry,
          }),
        });

        const data = await response.json();

        if (response.ok) {
          const modelId = data.modelId;

          // Procurar o mesh na scene
          this.scene.traverse((object) => {
            if (
              object.isMesh &&
              object.userData.fromFile &&
              object.userData.modelType === modelName
            ) {
              object.userData.modelId = modelId;
              console.log(
                `ModelId ${modelId} added to mesh with modelType ${modelName}`
              );
            }
          });
        } else {
          console.error("Erro ao salvar o modelo:", data);
        }
      } catch (error) {
        console.error("Erro ao salvar o modelo:", error);
      }
    },
    createThreeJsGeometry(vertices, name, modelId) {
      const geometry = new THREE.BufferGeometry();
      const positions = new Float32Array(vertices);
      geometry.setAttribute(
        "position",
        new THREE.BufferAttribute(positions, 3)
      );

      geometry.computeVertexNormals();
      geometry.computeBoundsTree();
      this.calculateVertexNormals(geometry);

      const material = new THREE.MeshPhongMaterial({
        color: 0x02bebe,
        specular: 0x000000,
        emissive: 0x000000,
        shininess: 0,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 1,
      });

      const mesh = new THREE.Mesh(geometry, material);
      mesh.userData = {
        modelType: name,
        name: this.$t(name),
        hide: false,
        modelId: modelId,
      };

      console.table(mesh.userData)

      // Adiciona o mesh à cena
      this.scene.add(mesh);

      // Adiciona um BoxHelper opcional
      const box = new THREE.Box3().setFromObject(mesh);
      const boxHelper = new THREE.Box3Helper(box, 0x000000);
      boxHelper.visible = false;
      boxHelper.userData = {
        boxHelper: name,
        name: this.$t("boxHelper"),
        hide: true,
      };
      this.scene.add(boxHelper);
      mesh.userData.helpers = { boxHelper };

      // Atualiza a cena e informações no store
      this.handleObjectClickFromList(mesh);
      this.$store.dispatch("editor3D/updateScene", this.scene);
      this.sceneUpdate = new Date();
      this.$store.dispatch("editor3D/updateDate", this.sceneUpdate);

      this.onWindowResize();
      this.$refs.progressDialog.cancelProcess();
      this.statusMessage = `Modelo ${modelId} carregado com sucesso`;
    },
    async dbLoadModel(modelId) {
      fetch(`${process.env.VUE_APP_API_URL}models/${modelId}`)
        .then((response) => response.json())
        .then((data) => {
          const buffer = data.geometry.data.attributes.position.array;
          this.createThreeJsGeometry(buffer, data.name, modelId, false);
        })
        .catch((error) => console.error("Erro ao recuperar o modelo:", error));
        let loadedDentalMap = await this.dbLoadDentalMap(modelId);
        this.dentalMap = { ...this.dentalMap, ...loadedDentalMap };
    },
    async dbDeleteDentalMap(modelId, all = false) {
      try {
        // Faz a requisição DELETE para o endpoint de exclusão do DentalMap
        const response = await fetch(
          `${process.env.VUE_APP_API_URL}models/${modelId}/dentalMap`,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ all }),
          }
        );

        this.$refs.progressDialog.cancelProcess();

        // Verifica se a requisição foi bem-sucedida
        if (!response.ok) {
          const errorMessage = await response.text();
          console.error("Erro ao deletar o DentalMap:", errorMessage);
          return;
        }

        // Se a exclusão for bem-sucedida
        console.log("DentalMap deletado com sucesso!");
      } catch (error) {
        console.error("Erro ao deletar o DentalMap:", error);
      }
    },
    loadModel(params) {
      const loader = new STLLoader();

      fetch("./simplify/worker0.2.51.js")
        .then((response) => response.blob())
        .then((blob) => {
          const workerURL = URL.createObjectURL(blob);
          const worker = new Worker(workerURL);

          worker.onmessage = (e) => {
            if (e.data.status && e.data.status === "ready") {
              console.warn("Worker ready");
              console.table(params.file);
              worker.postMessage({
                blob: params.file,
                percentage: 0.4,
                simplify_name: "simplified_model.stl",
              });
            }
            const file = e.data.blob;
            if (file !== undefined) {
              const reader = new FileReader();
              reader.readAsArrayBuffer(file);
              reader.onload = (e) => {
                const buffer = e.target.result;
                const geometry = loader.parse(buffer);

                //this.dbSaveModel(params.modelType, params.file.name, params.file.size, geometry);
                this.dbSearchModel(
                  params.modelType,
                  params.file.name,
                  params.file.size,
                  geometry
                );

                this.rotateGeometry(geometry, -Math.PI / 2);

                //console.table(this.determineAlignmentAndFrontDirection(geometry))

                geometry.computeBoundsTree();
                this.calculateVertexNormals(geometry);

                const material = new THREE.MeshPhongMaterial({
                  color: 0x02bebe,
                  specular: 0x000000,
                  emissive: 0xaaaaaa,
                  shininess: 0,
                  side: THREE.DoubleSide,
                  transparent: true,
                  opacity: 1,
                });

                const mesh = new THREE.Mesh(geometry, material);
                mesh.userData = {
                  modelType: params.modelType,
                  name: this.$t(params.modelType),
                  hide: false,
                  fromFile: true,
                };

                this.setMeshCategory(mesh, "base");

                this.scene.add(mesh);

                // Box3Helper
                const box = new THREE.Box3().setFromObject(mesh);
                const boxHelper = new THREE.Box3Helper(box, 0xffff00); // cor amarela para a caixa
                boxHelper.visible = false;
                boxHelper.userData = {
                  boxHelper: params.modelType,
                  name: this.$t("boxHelper"),
                  hide: true,
                };

                this.setMeshCategory(boxHelper, "aux");

                //this.scene.add(boxHelper);

                // Armazenando referências aos helpers no userData do mesh para fácil acesso
                mesh.userData.helpers = {
                  boxHelper: boxHelper,
                };

                this.handleObjectClickFromList(mesh);

                this.$store.dispatch("editor3D/updateScene", this.scene);
                this.sceneUpdate = new Date();
                this.$store.dispatch("editor3D/updateDate", this.sceneUpdate);

                // Termina o worker e libera o URL do blob
                worker.terminate();
                URL.revokeObjectURL(workerURL);
                this.onWindowResize();
                this.$refs.progressDialog.cancelProcess();

                // const meanDetected = this.getMeanCurvature(mesh).toFixed(2);
                // const meanText =
                //   meanDetected < 7
                //     ? "Modelo com baixa curvatura, possivel dificuldade na segmentação detectada"
                //     : "Modelo com curvatura moderada, chances de segmentação com sucesso aumentada.";

                // this.$refs.progressDialog.showProgressDialog(
                //   "Verificando qualidade do modelo...",
                //   10
                // );
                // setTimeout(() => {
                //   this.$refs.progressDialog.cancelProcess();
                //   this.$refs.progressDialog.showProgressDialog(
                //     `${meanText} - Curvatura em ${meanDetected}° graus.`,
                //     10
                //   );
                // }, 2000);
                // setTimeout(() => {
                //   this.$refs.progressDialog.cancelProcess();
                // }, 8000);
              };

              return;
            }
          };
        })
        .catch((error) => console.error("Erro ao carregar o worker:", error));
    },
    onWindowResize() {
      const container = document.querySelector(".threejs-canvas-container");

      const width = container.clientWidth;
      const height = container.clientHeight;
      const needResize =
        this.renderer.domElement.width !== width ||
        this.renderer.domElement.height !== height;
      if (needResize) {
        this.renderer.setSize(width, height);
        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();
      }
    },
    lerpColor(color1, color2, t) {
      const c1 = new THREE.Color(color1);
      const c2 = new THREE.Color(color2);
      return c1.lerp(c2, t);
    },
    animateBackgroundColor(toColorHex, duration = 500) {
      const fromColor = new THREE.Color(this.renderer.getClearColor());
      const toColor = new THREE.Color(toColorHex);

      const start = performance.now();

      const animate = (now) => {
        const elapsed = now - start;
        const t = Math.min(elapsed / duration, 1); // Normaliza o tempo (0 → 1)

        const current = fromColor.clone().lerp(toColor, t);
        this.renderer.setClearColor(current);

        if (t < 1) {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
    },
    cloneOcclusionPlanForCutting() {
      // Encontrar o plano de oclusão na cena
      const occlusionPlane = this.scene.children.find(
        (child) => child.userData.isOcclusionPlan === true
      );

      // Se o plano de oclusão for encontrado, clonar para criar o plano de corte
      if (occlusionPlane) {
        const cuttingPlane = occlusionPlane.clone();
        cuttingPlane.userData = {
          name: this.$t("cuttingPlane"),
          hide: false,
          isCuttingPlane: true,
          normal: occlusionPlane.userData.normal,
        };

        // Adicionar o cuttingPlane à cena e aplicar o TransformControl a ele
        this.scene.add(cuttingPlane);
        this.$store.dispatch("editor3D/updateScene", this.scene);
        this.sceneUpdate = new Date();
        this.$store.dispatch("editor3D/updateDate", this.sceneUpdate);

        this.transformControl.attach(cuttingPlane);
        this.transformControl.setMode("translate"); // ou outro modo conforme necessário

        // Adicionar o indicador de direção
        //this.addDirectionIndicatorToPlane(cuttingPlane);

        // Ouvinte para atualizar a cena quando o TransformControl é manipulado
        this.transformControl.addEventListener("change", () => {
          this.cuttingPlanePosition = cuttingPlane.position;
          // Outras ações necessárias após a movimentação do plano
        });

        // Não esqueça de adicionar o TransformControl à cena se ainda não tiver sido adicionado
        if (!this.scene.children.includes(this.transformControl)) {
          this.scene.add(this.transformControl);
        }
      } else {
        console.error("Plano de oclusão não encontrado na cena.");
      }
    },
    addDirectionIndicatorToPlane(cuttingPlane) {
      const cylinderHeight = 5;
      const coneHeight = 1;
      const radius = 0.75;

      // A normal é clonada diretamente do userData do plano de corte
      const planeNormal = cuttingPlane.userData.normal.clone();

      // Cria o cilindro e o cone
      const cylinderGeometry = new THREE.CylinderGeometry(
        radius,
        radius,
        cylinderHeight,
        32
      );
      const cylinderMaterial = new THREE.MeshBasicMaterial({ color: 0xffff00 });
      const cylinder = new THREE.Mesh(cylinderGeometry, cylinderMaterial);

      const coneGeometry = new THREE.ConeGeometry(radius, coneHeight, 32);
      const coneMaterial = new THREE.MeshBasicMaterial({ color: 0xff0000 });
      const cone = new THREE.Mesh(coneGeometry, coneMaterial);

      // Ajustar a posição do cone para a extremidade do cilindro
      cone.position.set(0, cylinderHeight / 2 + coneHeight / 2, 0);

      // Agrupar o cilindro e o cone para formar a seta
      const arrow = new THREE.Group();
      arrow.add(cylinder);
      arrow.add(cone);

      // Posicionar a seta acima do plano de corte para visibilidade
      const offset = 1; // Define o quanto a seta estará acima do plano
      arrow.position.copy(planeNormal.clone().multiplyScalar(offset));

      // Direcionar a seta para cima ao longo da normal do plano
      arrow.lookAt(planeNormal);

      // Adicionar a seta como um filho do plano de corte
      cuttingPlane.add(arrow);

      // Se a normal estiver apontando para baixo, inverter a orientação da seta
      if (planeNormal.y < 0) {
        arrow.rotateX(Math.PI);
      }
    },
    createOcclusionPlan(params) {
      // Extracts the points and objectRef from params
      const { points, objectRef } = params;

      // Assumes that points[0], points[1], and points[2] are the THREE.Vector3 points
      const [point1, point2, point3] = points;

      // Calculates the plane's normal
      const normal = new THREE.Vector3()
        .crossVectors(
          new THREE.Vector3().subVectors(point2, point1),
          new THREE.Vector3().subVectors(point3, point1)
        )
        .normalize();

      // Calculates the centroid of the three points
      const centroid = new THREE.Vector3(
        (point1.x + point2.x + point3.x) / 3,
        (point1.y + point2.y + point3.y) / 3,
        (point1.z + point2.z + point3.z) / 3
      );

      // Defines the size and material of the plane
      const size = params.size || 40;
      const geometry = new THREE.CircleGeometry(size, 64);
      const material = new THREE.MeshPhongMaterial({
        color: 0xffa726,
        specular: 0x222222,
        shininess: 400,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.5,
      });

      // Creates the plane
      const plane = new THREE.Mesh(geometry, material);
      plane.lookAt(normal);
      plane.position.copy(centroid);

      // Adds information to the userData of the plane
      plane.userData = {
        name: this.$t("occlusionPlan"),
        hide: false, // Initially hidden, assuming that's the desired logic
        objectRef: objectRef, // Stores the reference to the original object
        isOcclusionPlan: true,
        normal: normal,
      };

      // Adds the plane to the scene
      this.scene.add(plane);
      this.$store.dispatch("editor3D/updateScene", this.scene);
      this.sceneUpdate = new Date();
      this.$store.dispatch("editor3D/updateDate", this.sceneUpdate);

      //const cilinders = createMedialDistalCylinders(this.dentalMap, plane)
      //this.scene.add(cilinders)
      // const { projectionGroup, contours, heatmapGroup } = projectVerticesToPlane(
      //   this.activeObject,
      //   plane,
      //   this.dentalMap,
      //   {
      //     clusterEps: 0.5, // Distância máxima entre pontos do mesmo cluster
      //     minClusterPoints: 8,
      //     circularityThreshold: 0.8,
      //     scene: this.scene,
      //     debug: true, // Tolerância de circularidade
      //   }
      // );
      // this.scene.add(heatmapGroup);
      // extractToothCrownVertices(this.activeObject,
      //    this.dentalMap);

      // Returns the created plane
      return plane;
    },
    distanceBetweenPoints(pointA, pointB) {
      let dx = pointA.x - pointB.x;
      let dz = pointA.z - pointB.z;
      return Math.sqrt(dx * dx + dz * dz);
    },
    degreesToRadians(degrees) {
      return degrees * (Math.PI / 180);
    },
    adjustAngleTowards90(actualAngle) {
      // Converte o ângulo para distância de 90 graus
      const distanceTo90 = Math.abs(actualAngle - 90);

      // Se o ângulo estiver a mais de 1 grau de distância de 90, ajusta de 1 grau em direção a 90
      if (distanceTo90 > 2) {
        return actualAngle + (actualAngle < 90 ? 2 : -2);
      }

      return actualAngle;
    },
    generateGumCurve(teethCenters) {
      if (!this.activeObject || !this.activeObject.userData.modelType)
        return [];

      const isUpperArch = this.activeObject.userData.modelType === "maxillary";
      const teeth = [];

      // Separa e ordena os centroids com base no número do dente
      Object.keys(teethCenters).forEach((toothNumber) => {
        const toothInt = parseInt(toothNumber);
        if (
          isUpperArch
            ? toothInt >= 11 && toothInt <= 28
            : toothInt >= 31 && toothInt <= 48
        ) {
          teeth.push({
            toothNumber: toothInt,
            centerOfMass: teethCenters[toothNumber],
          });
        }
      });

      // Ordena os dentes superiores e inferiores
      teeth.sort((a, b) => a.toothNumber - b.toothNumber);

      // Define a ordem específica para os dentes da arcada superior e inferior
      const upperTeethOrder = [
        18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28,
      ];
      const lowerTeethOrder = [
        48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38,
      ];

      // Função para ordenar os dentes conforme a ordem definida
      const orderTeeth = (teeth, order) => {
        return order
          .map((toothNumber) =>
            teeth.find((tooth) => tooth.toothNumber === toothNumber)
          )
          .filter((tooth) => tooth !== undefined);
      };

      // Verifica se é arcada superior para usar a ordem correspondente
      const orderedTeeth = isUpperArch
        ? orderTeeth(teeth, upperTeethOrder)
        : orderTeeth(teeth, lowerTeethOrder);

      // Filtra possíveis valores nulos e cria os vetores de posição para a curva Catmull-Rom
      const curvePoints = orderedTeeth
        .map((tooth) => tooth.centerOfMass)
        .filter((centerOfMass) => centerOfMass.centerOfMass !== undefined)
        .map(
          (tooth) =>
            new THREE.Vector3(
              tooth.centerOfMass.x,
              tooth.centerOfMass.y,
              tooth.centerOfMass.z
            )
        );

      // Cria a curva Catmull-Rom
      const curve = new THREE.CatmullRomCurve3(
        curvePoints,
        false,
        "centripetal",
        0.5
      );

      // Gera os pontos da curva com 50 divisões entre cada par de centroids
      const points = curve.getPoints(curvePoints.length * 24);

      // Crie o shape da gengiva com um perfil mais anatômico
      const shape = new THREE.Shape();
      const width = 1.2; // Ajuste para a largura desejada da base
      const height = isUpperArch ? -4 : 4; // A altura do arco da gengiva

      // Mova para a posição inicial (canto esquerdo inferior do "U" invertido)
      shape.moveTo(-5 * width, 5 * height);

      // Primeiro, desenhe a curva de Bézier indo para baixo da base (se desejar uma curva)
      shape.bezierCurveTo(
        -2 * width,
        2.5 * height,
        1.5 * width,
        2.5 * height,
        5 * width,
        5 * height
      );

      // Adicione uma linha reta para o lado direito, formando a base do "U"
      shape.lineTo(-5 * width, 5 * height);

      // Chamada do método para criar a extrusão
      const extrudedMesh = this.extrudeAlongCurve(points, shape);
      if (isUpperArch) {
        extrudedMesh.geometry.translate(-2, 3.2 * height, -1.5 * width);
        extrudedMesh.geometry.rotateX(this.degreesToRadians(-5));
      } else {
        extrudedMesh.geometry.translate(2, 2.75 * height, -6 * width);
      }

      this.replaceGumGeometry(extrudedMesh.geometry, isUpperArch);

      return points;
    },
    replaceGumGeometry(newGumGeometry, isUpperArch) {
      // Encontra o objeto gengiva na cena.
      const modelType = isUpperArch ? "maxillary" : "mandibular";
      const gumsInScene = this.findGumsInScene();
      const gumInScene = gumsInScene.filter(
        (gum) => gum.userData.modelType === modelType
      )[0];

      if (gumInScene) {
        // Remove a geometria antiga da memória
        if (gumInScene.geometry) {
          gumInScene.geometry.dispose();
        }

        // Substitui a geometria antiga pela nova
        gumInScene.geometry = newGumGeometry;
        if (gumInScene.scale.x === 1) {
          gumInScene.scale.x *= isUpperArch ? 0.999 : 1.2;
          gumInScene.scale.z *= 1.2;
        }

        // Aviso: Se as propriedades como a posição, rotação ou escala precisarem ser ajustadas,
        // você deverá fazê-lo após substituir a geometria.
      } else {
        console.error("Gengiva não encontrada na cena.");
      }
    },
    extrudeAlongCurve(points, shapeGeometry) {
      // Cria a curva Catmull-Rom a partir dos pontos
      const curve = new THREE.CatmullRomCurve3(points);

      // Define as configurações da extrusão
      const extrudeSettings = {
        steps: 64, // Número de pontos ao longo da curva para construir a extrusão
        bevelEnabled: false, // Desativa o chanfro
        extrudePath: curve, // A curva ao longo da qual a extrusão ocorrerá
      };

      // Cria a geometria de extrusão
      const extrudeGeometry = new THREE.ExtrudeGeometry(
        shapeGeometry,
        extrudeSettings
      );

      // Cria o material
      const material = new THREE.MeshPhongMaterial({
        color: 0xffc0cb,
        specular: 0x333366,
        shininess: 100,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 1,
      });

      // Cria a mesh de extrusão
      const extrudedObject = new THREE.Mesh(extrudeGeometry, material);
      //this.calculateVertexNormals(extrudeGeometry)

      // Retorna a mesh
      return extrudedObject;
    },
  },
};
